import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';

import { API, postRequest } from '@/api/api.ts';
import { cardImportRefusalMessage } from '@/common/error.ts';
import { toast } from '@/utils/toast.ts';

import {
  createVendorMapping,
  getVendorMappings,
  removeVendorMapping,
  updateVendorMapping,
} from './imported-transactions.api.ts';
import type {
  AnalyzedTransactions,
  CreateVendorMappingBody,
  UpdateVendorMappingBody,
  VendorMapping,
} from './transactions.dto.ts';

const IMPORT_FAILED_MESSAGE = 'Couldn’t import the file. Please try again.';
const IMPORT_ERROR_TOAST_DURATION = 10_000;

export const EXTRACTED_TRANSACTIONS_STORAGE_KEY = 'extracted-transactions';

const extractedTransactionsStorage: StateStorage = {
  getItem: (name) => sessionStorage.getItem(name),
  setItem: (name, value) => {
    try {
      const parsed = JSON.parse(value);
      if (parsed?.state?.extractedTransactions == null) {
        sessionStorage.removeItem(name);
        return;
      }
    } catch (error) {
      console.error(error);
    }
    sessionStorage.setItem(name, value);
  },
  removeItem: (name) => sessionStorage.removeItem(name),
};

type ImportedTransactionsStore = {
  extractedTransactions: AnalyzedTransactions | null;
  isCsvImporting: boolean;
  vendorMappings: VendorMapping[];
  vendorMappingsLoading: boolean;
  vendorMappingsError: string | null;
  importTransactionFile: (file: File) => Promise<boolean>;
  clearExtractedTransactions: () => void;
  fetchVendorMappings: () => Promise<void>;
  createVendorMapping: (body: CreateVendorMappingBody) => Promise<VendorMapping>;
  updateVendorMapping: (id: string, body: UpdateVendorMappingBody) => Promise<VendorMapping>;
  removeVendorMapping: (id: string) => Promise<void>;
  importLoading: boolean;
  importError: string | null;
};

export const useImportedTransactionsStore = create<ImportedTransactionsStore>()(
  persist(
    (set, _get, api) => ({
      extractedTransactions: null,
      isCsvImporting: false,
      importLoading: false,
      importError: null,
      vendorMappings: [],
      vendorMappingsLoading: false,
      vendorMappingsError: null,
      importTransactionFile: async (file) => {
        set({ isCsvImporting: true, importLoading: true, importError: null });
        try {
          const formData = new FormData();
          formData.append('file', file);
          const response = await postRequest<AnalyzedTransactions, FormData>(
            API.EXPENSES,
            '/card-import/analyze',
            formData,
            true,
          );
          const data = response?.data;

          if (!data || !data.rows || data.rows.length === 0) {
            toast.info('This file has no transactions. Please try another.');
            set({ extractedTransactions: null, importLoading: false });
            return false;
          }
          set({ extractedTransactions: { ...data }, importLoading: false });
          return true;
        } catch (error) {
          console.error(error);
          const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
          const message = cardImportRefusalMessage(error, isPdf) ?? IMPORT_FAILED_MESSAGE;
          set({ importError: message, importLoading: false });
          toast.error(message, { duration: IMPORT_ERROR_TOAST_DURATION });
          return false;
        } finally {
          set({ isCsvImporting: false });
        }
      },
      clearExtractedTransactions: () => {
        set({ extractedTransactions: null });
        api.persist.clearStorage();
      },
      fetchVendorMappings: async () => {
        set({ vendorMappingsLoading: true, vendorMappingsError: null });
        try {
          const mappings = await getVendorMappings();
          set({ vendorMappings: mappings, vendorMappingsLoading: false });
        } catch (error) {
          const message =
            error instanceof Error ? error.message : "Couldn't load the vendor mappings. Try again";
          set({ vendorMappingsError: message, vendorMappingsLoading: false });
        }
      },
      createVendorMapping: async (body) => {
        const created = await createVendorMapping(body);
        set((state) => ({ vendorMappings: [...state.vendorMappings, created] }));
        return created;
      },
      updateVendorMapping: async (id, body) => {
        const updated = await updateVendorMapping(id, body);
        set((state) => ({
          vendorMappings: state.vendorMappings.map((m) => (m.id === id ? updated : m)),
        }));
        return updated;
      },
      removeVendorMapping: async (id) => {
        await removeVendorMapping(id);
        set((state) => ({
          vendorMappings: state.vendorMappings.filter((m) => m.id !== id),
        }));
      },
    }),
    {
      name: EXTRACTED_TRANSACTIONS_STORAGE_KEY,
      storage: createJSONStorage(() => extractedTransactionsStorage),
      partialize: (state) => ({ extractedTransactions: state.extractedTransactions }),
    },
  ),
);
